import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { SendEmailRequest, SendEmailResponse } from '@/types/email';

// Business-rule rejections come back as 200 + { error, code } so we can show
// the real reason instead of the generic non-2xx message.
const REJECTION_TITLES: Record<string, string> = {
  suppressed: "Can't send to this address",
  quota_exceeded: 'Monthly email limit reached',
  sending_paused: 'Email sending is paused',
  disabled: 'Email is not enabled',
};

export class EmailRejectedError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'EmailRejectedError';
    this.code = code;
  }
}

// Hook to send emails via the send-email edge function
export function useSendEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SendEmailRequest): Promise<SendEmailResponse> => {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: request,
      });

      if (error) {
        throw new Error(error.message || 'Failed to send email');
      }

      const res = data as SendEmailResponse | null;
      if (res?.error) {
        throw new EmailRejectedError(res.code ?? 'provider_error', res.error);
      }

      return res as SendEmailResponse;
    },
    onSuccess: () => {
      toast.success('Email sent successfully');
      // Invalidate omni-channel queries to refresh the thread
      queryClient.invalidateQueries({ queryKey: ['omni-channel-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-messages'] });
    },
    onError: (error: Error) => {
      console.error('Failed to send email:', error);
      const title = error instanceof EmailRejectedError ? REJECTION_TITLES[error.code] : undefined;
      if (title) {
        toast.error(title, { description: error.message });
      } else {
        toast.error(error.message || 'Failed to send email');
      }
    },
  });
}
